import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../../lib/supabase';
import { Badge, Button, Card, Dropdown, Input, StatCard, Table, TableColumn, useToast } from '../../../ui';
import { setPageOffer, type Offer } from '../../../../lib/offers';
import PageTrendCharts from './PageTrendCharts';
import OveroptCard from './OveroptCard';
import TemplateCard from './TemplateCard';
import RetroOptJournal from './RetroOptJournal';
import SortHeader, { type SortState } from '../functions/SortHeader';
import { sortRows } from '../functions/sortRows';
import { ctrOf, fmtDay, fmtDec, fmtInt, fmtPct, fmtWhen, pageFlags, type PageHistoryRow, type PageQueryRow, type PageRow } from './format';

type QuerySortField = 'query' | 'clicks' | 'impressions' | 'ctr' | 'position';

const faint: React.CSSProperties = { color: 'var(--text-faint)' };
// The table sits inside a card, so it drops its own card chrome.
const flatTable: React.CSSProperties = { background: 'transparent', border: 'none', borderRadius: 0, boxShadow: 'none', minWidth: '520px' };

type Delta = { delta?: string; deltaTone?: 'up' | 'down' };

/** "+12%" against the window before; nothing when it didn't move or there is nothing to compare with. */
const change = (now: number | null, before: number | null): Delta => {
  if (!before) return now ? { delta: 'new', deltaTone: 'up' } : {};
  const pct = Math.round((100 * ((now ?? 0) - before)) / before);
  return pct === 0 ? {} : { delta: `${pct > 0 ? '+' : '-'}${Math.abs(pct)}%`, deltaTone: pct > 0 ? 'up' : 'down' };
};

/** Position moves the other way: a smaller number is the improvement, so that is the "up". */
const positionChange = (now: number | null, before: number | null): Delta => {
  if (now == null || before == null) return {};
  const diff = Number(now) - Number(before);
  return Math.abs(diff) < 0.05 ? {} : { delta: Math.abs(diff).toFixed(1), deltaTone: diff < 0 ? 'up' : 'down' };
};

interface PageDetailViewProps {
  client: { id: string; name: string };
  page: PageRow;
  path: string;
  /** The client's offers (denied ones left out), for the offer this page sells. */
  offers?: Offer[];
  /** Called with the saved row when something on the page changes here (its offer). */
  onPageChange?: (page: PageRow) => void;
}

/**
 * One URL in depth: how it did in search over the window (against the window before),
 * its weekly trend, every query it showed for, and what the crawl found on the page.
 */
export default function PageDetailView({ client, page, path, offers = [], onPageChange }: PageDetailViewProps) {
  const toast = useToast();
  const [savingOffer, setSavingOffer] = useState(false);
  const changeOffer = async (offerId: string) => {
    setSavingOffer(true);
    try {
      onPageChange?.(await setPageOffer(page.id, offerId || null));
    } catch (err) {
      toast.error('Couldn’t change the offer', err instanceof Error ? err.message : 'Nothing was saved.');
    } finally {
      setSavingOffer(false);
    }
  };
  const [queries, setQueries] = useState<PageQueryRow[]>([]);
  const [history, setHistory] = useState<PageHistoryRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState<QuerySortField>>({ field: 'impressions', dir: 'desc' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        setError(null);
        const [q, h] = await Promise.all([
          supabase.from('client_page_queries').select('*').eq('page_id', page.id).order('impressions', { ascending: false, nullsFirst: false }),
          supabase.from('client_page_history').select('*').eq('page_id', page.id).order('period_start'),
        ]);
        if (q.error) throw q.error;
        if (h.error) throw h.error;
        if (cancelled) return;
        setQueries(q.data ?? []);
        setHistory(h.data ?? []);
      } catch (err) {
        console.error('Error fetching page detail:', err);
        if (!cancelled) setError('Failed to load this page’s search data');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page.id]);

  const visibleQueries = useMemo(() => {
    const term = search.trim().toLowerCase();
    return sortRows(term ? queries.filter((q) => q.query.toLowerCase().includes(term)) : queries, sort);
  }, [queries, search, sort]);

  const flags = pageFlags(page);
  const header = (label: string, field: QuerySortField) => <SortHeader label={label} field={field} sort={sort} onSort={setSort} />;

  const queryColumns: TableColumn<PageQueryRow>[] = [
    { key: 'query', label: header('Query', 'query'), width: '46%', render: (v) => <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>{String(v)}</span> },
    { key: 'clicks', label: header('Clicks', 'clicks'), align: 'right', mono: true, render: (v) => fmtInt(v as number | null) },
    { key: 'impressions', label: header('Impr', 'impressions'), align: 'right', mono: true, render: (v) => fmtInt(v as number | null) },
    { key: 'ctr', label: header('CTR', 'ctr'), align: 'right', mono: true, render: (v) => fmtPct(v as number | null) },
    { key: 'position', label: header('Pos', 'position'), align: 'right', mono: true, render: (v) => fmtDec(v as number | null) },
  ];

  const facts: [string, React.ReactNode][] = [
    ['Status', page.in_crawl ? (page.http_code ?? '—') : 'Not found as a live page in the last crawl'],
    ['Indexable', page.in_crawl ? (page.is_noindex ? 'No — noindex' : page.is_indexable === false ? 'No' : 'Yes') : '—'],
    [
      'Canonical',
      !page.canonical ? '—' : page.canonical === page.url ? 'Self' : (
        <a href={page.canonical} target="_blank" rel="noreferrer" style={{ color: 'var(--accent-text)' }}>{page.canonical}</a>
      ),
    ],
    ['Click depth', fmtInt(page.depth)],
    ['Words', fmtInt(page.word_count)],
    ['Internal links in', fmtInt(page.inlinks)],
    ['Ahrefs est. traffic', page.ahrefs_traffic == null ? '—' : `${fmtInt(page.ahrefs_traffic)} / mo`],
    [
      'Ahrefs top keyword',
      page.ahrefs_top_keyword ? `${page.ahrefs_top_keyword}${page.ahrefs_top_position ? ` · #${page.ahrefs_top_position}` : ''}` : '—',
    ],
  ];

  const empty = (text: string) => <div style={{ padding: '28px 0', textAlign: 'center', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{text}</div>;

  return (
    <>
      <Card
        eyebrow="Page"
        title={<span style={{ overflowWrap: 'anywhere' }}>{path}</span>}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {offers.length ? (
              <span title="The offer this page sells" style={{ opacity: savingOffer ? 0.6 : 1 }}>
                <Dropdown
                  size="sm"
                  value={page.offer_id ?? ''}
                  onChange={(e) => void changeOffer(e.target.value)}
                  options={[{ value: '', label: 'No offer' }, ...offers.map((o) => ({ value: o.id, label: `Offer: ${o.name}` }))]}
                  style={{ width: '220px' }}
                />
              </span>
            ) : null}
            <a href={page.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
              <Button variant="secondary" size="sm" icon="external-link">Open page</Button>
            </a>
          </div>
        }
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {page.tier ? (
            <span title={page.tier_reason ?? ''}>
              <Badge tone={page.tier === 'A' ? 'accent' : 'neutral'}>{page.tier === 'A' ? 'A · money page' : 'B · other page'}</Badge>
            </span>
          ) : null}
          {page.title || 'No title found'}
          {flags.map((flag) => (
            <Badge key={flag} tone="warn">{flag}</Badge>
          ))}
        </div>
        <div className="t-num" style={{ marginTop: '4px', fontSize: 'var(--text-xs)', ...faint }}>
          {page.gsc_date_from
            ? `Search Console ${fmtDay(page.gsc_date_from)} – ${fmtDay(page.gsc_date_to)}, compared with the period before`
            : 'Search Console isn’t connected to this site’s Ahrefs project'}{' '}
          · synced {fmtWhen(page.synced_at)}
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--space-4)' }}>
        <StatCard label="Clicks" value={fmtInt(page.gsc_clicks)} hint={`${fmtInt(page.gsc_clicks_prev)} before`} {...change(page.gsc_clicks, page.gsc_clicks_prev)} />
        <StatCard label="Impressions" value={fmtInt(page.gsc_impressions)} hint={`${fmtInt(page.gsc_impressions_prev)} before`} {...change(page.gsc_impressions, page.gsc_impressions_prev)} />
        <StatCard label="CTR" value={fmtPct(page.gsc_ctr)} hint={`${fmtPct(ctrOf(page.gsc_clicks_prev, page.gsc_impressions_prev))} before`} />
        <StatCard label="Avg position" value={fmtDec(page.gsc_position)} hint={`${fmtDec(page.gsc_position_prev)} before`} {...positionChange(page.gsc_position, page.gsc_position_prev)} />
        <StatCard label="Queries" value={fmtInt(page.gsc_queries)} hint={`${fmtInt(page.gsc_queries_prev)} before`} {...change(page.gsc_queries, page.gsc_queries_prev)} />
      </div>

      {error ? <div style={{ color: 'var(--danger-400)', fontSize: 'var(--text-sm)' }}>{error}</div> : null}

      <OveroptCard page={page} />

      <TemplateCard page={page} />

      <RetroOptJournal client={client} page={page} history={history} queries={queries} />

      {/* Trend beside the crawl facts; the facts drop underneath when the column is narrow */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-4)', alignItems: 'flex-start' }}>
        <div style={{ flex: '3 1 460px', minWidth: 0 }}>
          {isLoading ? (
            <Card eyebrow="Search Console" title="Weekly trend">{empty('Loading...')}</Card>
          ) : (
            <PageTrendCharts history={history} />
          )}
        </div>
        <Card eyebrow="Site Audit" title="On the page" style={{ flex: '1 1 260px', minWidth: 0 }}>
          <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: '8px 16px', fontSize: 'var(--text-sm)' }}>
            {facts.map(([label, value]) => (
              <div key={label} style={{ display: 'contents' }}>
                <dt style={{ ...faint, whiteSpace: 'nowrap' }}>{label}</dt>
                <dd style={{ margin: 0, color: 'var(--text-body)', overflowWrap: 'anywhere' }}>{value}</dd>
              </div>
            ))}
          </dl>
        </Card>
      </div>

      <Card
        eyebrow="Search Console"
        title={
          isLoading ? 'Queries' : visibleQueries.length === queries.length ? `${fmtInt(queries.length)} queries` : `${fmtInt(visibleQueries.length)} of ${fmtInt(queries.length)} queries`
        }
        actions={
          queries.length ? (
            <Input icon="search" fieldSize="sm" placeholder="Filter queries..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: '220px' }} />
          ) : null
        }
      >
        {isLoading ? (
          empty('Loading...')
        ) : queries.length === 0 ? (
          empty(
            !page.gsc_date_from
              ? 'No Search Console data for this site yet.'
              : page.gsc_impressions
                ? 'Queries weren’t pulled for this page in the last sync.'
                : 'Google hasn’t shown this page for anything in this window.'
          )
        ) : visibleQueries.length === 0 ? (
          empty('No queries match that filter.')
        ) : (
          <div style={{ overflowX: 'auto', margin: '0 calc(-1 * var(--space-2))' }}>
            <Table columns={queryColumns} rows={visibleQueries} dense style={flatTable} />
          </div>
        )}
      </Card>
    </>
  );
}
